import { findLanguage } from '@tolk/shared';
import type { Teksten } from '../teksten/nl';
import type { Verslag } from './api';

/**
 * Rekenwerk voor het gespreksverslag, los van het scherm zodat het te toetsen
 * is. Zie pages/VerslagPage.tsx voor de opmaak.
 *
 * DE TIJD IS DIE VAN DE LEZER. De server bewaart milliseconden in UTC; de
 * browser rekent ze om naar de klok van het toestel waarop het verslag gemaakt
 * wordt. Daarom staat de tijdzone er altijd achter: een verslag dat naar een
 * ouder in een ander land gaat, moet zeggen WELKE 14:02 het was.
 *
 * `tijdzone` is alleen voor de toetsen. Op het scherm blijft hij leeg en geldt
 * die van het toestel.
 */

/**
 * De taalcode met de GREGORIAANSE kalender erbij.
 *
 * Zonder dit schrijft 'ar-SA' de datum in de islamitische kalender: jaar 1448
 * in plaats van 2026. Voor een Saoedische lezer is dat gewoon, maar in een
 * verslag van een Nederlands oudergesprek naast een Nederlandse datum leest het
 * als een fout. De cijfers blijven wel die van de taal.
 */
function metKalender(taal: string): string {
  return `${taal}-u-ca-gregory`;
}

function opties(tijdzone: string | undefined, rest: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  return tijdzone === undefined ? rest : { ...rest, timeZone: tijdzone };
}

/** "woensdag 23 september 2026", in de taal en notatie van `taal`. */
export function datumVolledig(ms: number, taal: string, tijdzone?: string): string {
  return new Intl.DateTimeFormat(metKalender(taal), opties(tijdzone, { dateStyle: 'full' })).format(ms);
}

/** "14:02". */
export function klok(ms: number, taal: string, tijdzone?: string): string {
  return new Intl.DateTimeFormat(metKalender(taal), opties(tijdzone, { timeStyle: 'short' })).format(ms);
}

/** Datum en tijd samen, voor "gemaakt op" en "gewist vanaf". */
export function tijdstip(ms: number, taal: string, tijdzone?: string): string {
  return new Intl.DateTimeFormat(
    metKalender(taal),
    opties(tijdzone, { dateStyle: 'full', timeStyle: 'short' }),
  ).format(ms);
}

/** De naam van de tijdzone op dat moment, bijvoorbeeld "CEST" of "GMT+2". */
export function tijdzoneNaam(ms: number, taal: string, tijdzone?: string): string {
  const delen = new Intl.DateTimeFormat(
    metKalender(taal),
    opties(tijdzone, { timeZoneName: 'short' }),
  ).formatToParts(ms);
  return delen.find((d) => d.type === 'timeZoneName')?.value ?? '';
}

/**
 * De naam van een taal, geschreven in een andere taal: Hongaars in het
 * Nederlands is "Hongaars", in het Hongaars "magyar".
 *
 * Intl kent die namen al voor elke taal die we ondersteunen. Valt het toch weg,
 * dan de eigen naam uit de talenlijst, en anders de code - nooit een lege cel.
 */
export function taalnaam(code: string, inTaal: string): string {
  const kort = code.split('-')[0] ?? code;
  try {
    const naam = new Intl.DisplayNames([inTaal], { type: 'language' }).of(kort);
    if (naam !== undefined && naam !== kort) return naam;
  } catch {
    // Oude browser zonder DisplayNames. De terugval hieronder is goed genoeg.
  }
  return findLanguage(code)?.eigenNaam ?? code;
}

/** "NL", "HU", "AR": het korte label boven een tekstcel. */
export function taalLabel(code: string): string {
  return (code.split('-')[0] ?? code).toUpperCase();
}

/**
 * Begin en eind van het gesprek: de eerste en de laatste zin.
 *
 * Zonder berichten het moment van aanmaken, voor allebei. Een kop met "14:02 –
 * 14:02" is eerlijker dan een gat.
 */
export function tijdspanne(verslag: Verslag): { begin: number; eind: number } {
  const eerste = verslag.berichten[0];
  const laatste = verslag.berichten[verslag.berichten.length - 1];
  if (eerste === undefined || laatste === undefined) {
    return { begin: verslag.kamer.createdAt, eind: verslag.kamer.createdAt };
  }
  return { begin: eerste.createdAt, eind: laatste.createdAt };
}

/**
 * Wat er in de vertaalcel komt.
 *
 * 'mislukt' ook als de status 'translated' is maar de tekst ontbreekt. Dat hoort
 * niet te gebeuren, maar een lege cel leest als "er is niets gezegd", en dat is
 * precies wat dit verslag niet mag suggereren.
 */
export function vertaalcel(
  bericht: Verslag['berichten'][number],
): { soort: 'tekst'; tekst: string } | { soort: 'mislukt' } | { soort: 'niet_klaar' } {
  if (bericht.status === 'pending') return { soort: 'niet_klaar' };
  if (bericht.status === 'failed' || bericht.translatedText === null || bericht.translatedText.trim() === '') {
    return { soort: 'mislukt' };
  }
  return { soort: 'tekst', tekst: bericht.translatedText };
}

/**
 * De eerlijkheidsnoot: wat "origineel" hier WEL en NIET betekent.
 *
 * WAAROM DIT ER STAAT. Met de spellingcorrectie aan vervangt de server de
 * verstane tekst door de verbeterde versie, en de ruwe tekst wordt nergens
 * bewaard (zie rooms/gesprek.ts op de server). Wie insprak met het
 * bevestigingsvenster, kan de tekst ook nog zelf hebben aangepast. Een verslag
 * dat "origineel" zegt zonder dit erbij, belooft een letterlijke opname die er
 * niet is.
 *
 * De correctiezin alleen als de correctie aanstond. De zin over aanpassen alleen
 * als er iets ingesproken is: bij typen is er niets "verstaan".
 */
export function nootZinnen(verslag: Verslag, t: Teksten): string[] {
  const zinnen = [t.verslagGeenOpname, t.verslagNootOrigineel];
  if (verslag.kamer.correctie) zinnen.push(t.verslagNootCorrectie);
  if (verslag.berichten.some((b) => b.ingesproken)) zinnen.push(t.verslagNootAangepast);
  return zinnen;
}

/**
 * De titel van de pagina, en daarmee de BESTANDSNAAM van de PDF: Chrome,
 * Safari en Firefox stellen bij "Opslaan als PDF" allemaal document.title voor.
 * Zonder dit heet elk verslag "Ask Gio Tolk.pdf".
 */
export function bestandsnaam(verslag: Verslag, tijdzone?: string): string {
  const { begin } = tijdspanne(verslag);
  // sv-SE schrijft een datum als 2026-09-23: sorteert goed in een map.
  const datum = new Intl.DateTimeFormat('sv-SE', opties(tijdzone, { dateStyle: 'short' })).format(begin);
  const naam = verslag.kamer.label.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return naam === '' ? `Gespreksverslag ${datum}` : `Gespreksverslag ${naam} ${datum}`;
}
